/**
 * Nomination Bracelet Cart Validation
 *
 * This script enforces the rule that Nomination bracelets (products with both
 * "NOMINATION" and "BRACELETS" tags) must be purchased with at least one
 * charm product (products with "CHARMS" tag) per bracelet.
 *
 * Rules:
 * - 1:1 ratio - each Nomination bracelet needs at least one charm
 * - CHARMS can be purchased standalone
 * - Only applies when Nomination bracelets are in cart
 * - Blocks checkout when rule is violated
 */

class NominationCartValidation {
  constructor() {
    // Normalize tag keys to lowercase for case-insensitive comparisons
    this.nominationBraceletTag = 'nomination';
    this.braceletTag = 'bracelets';
    this.charmsTag = 'charms';
    this.warningMessage =
      'Nomination bracelets must be purchased with at least one charm per bracelet. Please add <a href="/pages/search-results-page?q=nomination%20charms&page=1&rb_tags=Charm&rb_vendor=Nomination" style="color: #721c24; text-decoration: underline; font-weight: bold;">a charm</a> to continue.';
    this.debug = false; // Set to true for debugging
    this.showDebugTags = false; // Set to true to show tag badges on cart items (for testing only)

    // Add debounce for cart refresh to prevent infinite loops
    this.lastCartRefresh = 0;
    this.cartRefreshDebounce = 2000; // 2 seconds debounce to prevent conflicts
    this.lastValidation = 0;
    this.validationDebounce = 800; // 800ms debounce for validation to prevent conflicts
    this.isValidating = false; // Flag to prevent conflicts with other scripts

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupValidation());
    } else {
      this.setupValidation();
    }
  }

  setupValidation() {
    // Listen for cart updates
    this.listenForCartUpdates();

    // Initial validation check
    this.validateCart();

    // Remove continuous validation - only validate on specific events
    this.validationInterval = null;
    this.removeValidationInterval = null;

    // Validate when page becomes visible (user comes back to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(() => this.validateCart(), 100);
      }
    });

    // Validate when window regains focus
    window.addEventListener('focus', () => {
      setTimeout(() => this.validateCart(), 100);
    });
  }

  listenForCartUpdates() {
    // Wait for Cart Manager to be available
    if (!window.CartManager) {
      setTimeout(() => this.listenForCartUpdates(), 100);
      return;
    }

    // Use Cart Manager for all cart events
    this.cartManager = window.CartManager;

    // Listen for cart updates
    this.unsubscribeCartUpdated = this.cartManager.on(
      'cart:updated',
      () => {
        // Delay validation to avoid conflicts with engraving feature
        setTimeout(() => this.validateCart(true), 100);
      },
      this
    );

    // Listen for cart API calls
    this.unsubscribeCartApiCall = this.cartManager.on(
      'cart:api-call',
      (data) => {
        setTimeout(() => this.validateCart(), 20);
      },
      this
    );

    // Listen for quantity changes
    this.unsubscribeQuantityChange = this.cartManager.on(
      'cart:quantity-change',
      (data) => {
        // Delay validation to allow DOM to update and avoid conflicts
        setTimeout(() => this.validateCart(true), 50);
      },
      this
    );

    // Listen for item removal
    this.unsubscribeItemRemove = this.cartManager.on(
      'cart:item-remove',
      (data) => {
        setTimeout(() => this.forceCartRefresh(), 1500);
      },
      this
    );

    // Listen for DOM changes
    this.unsubscribeDomChanged = this.cartManager.on(
      'cart:dom-changed',
      () => {
        this.validateCart();
      },
      this
    );

    // Listen for form submissions
    this.unsubscribeFormSubmit = this.cartManager.on(
      'cart:form-submit',
      (data) => {
        this.validateCart();
      },
      this
    );

    // Prevent checkout when validation fails
    document.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest(
        'button[name="checkout"], #checkout, #CartDrawer-Checkout, .cart__checkout-button, button[form="cart"]'
      );
      if (checkoutButton && checkoutButton.hasAttribute('data-nomination-disabled')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });

    // Prevent form submission when checkout is disabled
    document.addEventListener('submit', (event) => {
      const checkoutButton = event.target.querySelector(
        'button[name="checkout"], #checkout, #CartDrawer-Checkout, .cart__checkout-button, button[form="cart"]'
      );
      if (checkoutButton && checkoutButton.hasAttribute('data-nomination-disabled')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });
  }

  validateCart(forceValidation = false) {
    // Check for conflicts with other cart systems
    if (window.engravingFeature?.state?.isProcessing || window.engravingFeature?.isEnforcingRules) {
      return;
    }

    // Add debouncing to prevent excessive validation calls, but allow forced validation
    const now = Date.now();
    if (!forceValidation && now - this.lastValidation < this.validationDebounce) {
      return;
    }
    this.lastValidation = now;

    // Set validating flag to prevent conflicts
    if (this.isValidating) {
      return;
    }
    this.isValidating = true;

    try {
      const cartItems = this.getCartItems();

      // Add stability check - ensure we have valid cart items
      if (!cartItems || cartItems.length === 0) {
        this.clearAllWarnings();
        this.enableCheckout();
        return;
      }

      const nominationBracelets = this.getNominationBracelets(cartItems);
      const charms = this.getCharms(cartItems);

      // Debug: Display tags on cart items (only if enabled)
      if (this.showDebugTags) {
        this.displayTagsOnCartItems(cartItems);
      }

      // Debug logging for cart drawer (temporary - remove after fixing)
      if (this.debug && this.isCartDrawerOpen()) {
        console.log('=== Cart Drawer Validation Debug ===');
        console.log('Total cart items:', cartItems.length);
        console.log(
          'Nomination bracelets found:',
          nominationBracelets.length,
          nominationBracelets.map((b) => b.title)
        );
        console.log(
          'Charms found:',
          charms.length,
          charms.map((c) => c.title)
        );
        cartItems.forEach((item) => {
          const tags = this.getProductTagsFromElement(item.element);
          console.log(`Item: ${item.title}, Tags:`, tags);
        });
      }

      // Check for cart count inconsistency
      const cartCountElement = document.querySelector('.cart-count-bubble, .cart-count, [data-cart-count]');
      const displayedCount = cartCountElement ? parseInt(cartCountElement.textContent) : null;
      const actualCount = cartItems.length;

      if (displayedCount !== null && displayedCount !== actualCount) {
        // Force a more aggressive refresh if counts don't match, but with debouncing
        const now = Date.now();
        if (now - this.lastCartRefresh > this.cartRefreshDebounce) {
          this.lastCartRefresh = now;
          setTimeout(() => this.forceCartRefresh(), 100);
        }
      }

      // Only validate if there are Nomination bracelets in cart
      if (nominationBracelets.length === 0) {
        this.clearAllWarnings();
        this.enableCheckout();
        return;
      }

      // Check if we have enough charms (1:1 ratio)
      const totalBraceletQuantity = this.getTotalBraceletQuantity(nominationBracelets);
      const totalCharmQuantity = this.getTotalCharmQuantity(charms);

      // Check if we have enough charms for all bracelets (1:1 ratio)
      if (totalCharmQuantity < totalBraceletQuantity) {
        // Only show warnings if not already showing to prevent flickering
        if (!this.warningsShouldBeShown) {
          this.showWarnings(nominationBracelets, totalBraceletQuantity, totalCharmQuantity);
        }
        this.disableCheckout();
      } else {
        this.forceClearWarnings();
        this.enableCheckout();
      }
    } catch (error) {
      console.error('Error during validation:', error);
      // If there's an error but warnings should be shown, try to maintain them
      if (this.warningsShouldBeShown) {
        setTimeout(() => this.validateCart(true), 100);
      }
    } finally {
      // Clear validating flag
      this.isValidating = false;

      // Always restore body overflow after validation to prevent frozen screen
      this.restoreBodyOverflow();
    }
  }

  getCartItems() {
    // Use Cart Manager if available, otherwise fallback to DOM parsing
    if (this.cartManager) {
      return this.cartManager.getCartItemsFromDOM();
    }

    // Fallback to original DOM parsing
    const cartItems = [];

    // Get items from main cart page
    const mainCartItems = document.querySelectorAll('#main-cart-items .cart-item');

    mainCartItems.forEach((item, index) => {
      const productTitle = item.querySelector('.cart-item__name')?.textContent?.trim();
      const quantity = parseInt(item.querySelector('input[name="updates[]"]')?.value || 0);
      const variantId = item.querySelector('input[name="updates[]"]')?.dataset?.quantityVariantId;
      const itemKey = item.getAttribute('data-cart-item-key') || item.getAttribute('data-index') || `main-${index}`;

      if (productTitle && quantity > 0) {
        cartItems.push({
          title: productTitle,
          quantity: quantity,
          variantId: variantId,
          itemKey: itemKey,
          element: item,
          isDrawer: false,
        });
      }
    });

    // Get items from cart drawer
    const drawerItems = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
    drawerItems.forEach((item, index) => {
      const productTitle = item.querySelector('.cart-item__name')?.textContent?.trim();
      const quantity = parseInt(item.querySelector('input[name="updates[]"]')?.value || 0);
      const variantId = item.querySelector('input[name="updates[]"]')?.dataset?.quantityVariantId;
      const itemKey = item.getAttribute('data-cart-item-key') || item.getAttribute('data-index') || index;

      if (productTitle && quantity > 0) {
        // Use unique cart item key to identify each line item separately
        // This ensures products with same title but different properties (e.g., engraving) are counted separately
        const existingItem = cartItems.find((cartItem) => cartItem.itemKey === itemKey);

        if (!existingItem) {
          cartItems.push({
            title: productTitle,
            quantity: quantity,
            variantId: variantId,
            itemKey: itemKey,
            element: item,
            isDrawer: true,
          });
        }
      }
    });

    return cartItems;
  }

  getNominationBracelets(cartItems) {
    return cartItems.filter((item) => {
      const productTags = this.getProductTagsFromElement(item.element);
      // Must have NOMINATION and BRACELET(S) tags (case-insensitive)
      return productTags.includes(this.nominationBraceletTag) && this.hasBraceletTag(productTags);
    });
  }

  getCharms(cartItems) {
    return cartItems.filter((item) => {
      const productTags = this.getProductTagsFromElement(item.element);
      // Must have CHARM(S) tag, but NOT both NOMINATION and BRACELET(S) tags (case-insensitive)
      return (
        this.hasCharmsTag(productTags) &&
        !(productTags.includes(this.nominationBraceletTag) && this.hasBraceletTag(productTags))
      );
    });
  }

  getProductTagsFromElement(element) {
    // Use Cart Manager if available, but always normalize case
    if (this.cartManager) {
      const tags = this.cartManager.getProductTagsFromElement(element) || [];
      return tags
        .map((tag) => String(tag).trim())
        .filter((tag) => tag.length > 0)
        .map((tag) => tag.toLowerCase());
    }

    // Fallback to original implementation
    if (!element) return [];

    const tagsString = element.getAttribute('data-product-tags');

    if (tagsString) {
      // Return all tags lowercased to avoid case sensitivity issues
      const tags = tagsString
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .map((tag) => tag.toLowerCase());
      return tags;
    }

    return [];
  }

  // Returns true if tags contain either "bracelet" or "bracelets"
  hasBraceletTag(productTags) {
    return productTags.includes(this.braceletTag) || productTags.includes('bracelet');
  }

  // Returns true if tags contain either "charm" or "charms"
  hasCharmsTag(productTags) {
    return productTags.includes(this.charmsTag) || productTags.includes('charm');
  }

  getTotalBraceletQuantity(nominationBracelets) {
    return nominationBracelets.reduce((total, item) => total + item.quantity, 0);
  }

  getTotalCharmQuantity(charms) {
    return charms.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Check if cart drawer is currently open
   */
  isCartDrawerOpen() {
    const cartDrawer = document.querySelector('#CartDrawer, cart-drawer, .cart-drawer');
    if (!cartDrawer) return false;

    return (
      cartDrawer.classList.contains('is-empty') === false &&
      (cartDrawer.classList.contains('active') ||
        cartDrawer.classList.contains('open') ||
        cartDrawer.offsetParent !== null)
    );
  }

  /**
   * Restore body overflow to fix frozen screen when cart drawer closes
   */
  restoreBodyOverflow() {
    try {
      // Remove overflow-hidden class from body
      document.body.classList.remove('overflow-hidden');

      // Also remove inline overflow style
      document.body.style.overflow = '';
      document.body.style.removeProperty('overflow');

      // Also check html element
      document.documentElement.classList.remove('overflow-hidden');
      document.documentElement.style.overflow = '';
      document.documentElement.style.removeProperty('overflow');
    } catch (error) {
      console.error('Failed to restore body overflow:', error);
    }
  }

  /**
   * Display tags on cart items for debugging
   */
  displayTagsOnCartItems(cartItems) {
    cartItems.forEach((item) => {
      const tags = this.getProductTagsFromElement(item.element);

      // Remove existing debug tag display
      const existingDebug = item.element.querySelector('.debug-tags-display');
      if (existingDebug) {
        existingDebug.remove();
      }

      // Create new debug tag display
      if (tags.length > 0) {
        const tagDisplay = document.createElement('div');
        tagDisplay.className = 'debug-tags-display';
        tagDisplay.style.cssText = `
            background: #f0f0f0;
            border: 1px solid #ddd;
            padding: 5px 8px;
            margin-top: 5px;
            font-size: 11px;
            border-radius: 3px;
            color: #333;
          `;

        // Color code the tags
        const tagBadges = tags
          .map((tag) => {
            const upper = String(tag).toUpperCase();
            let color = '#999';
            if (upper === 'NOMINATION') color = '#2563eb';
            if (upper === 'BRACELETS') color = '#7c3aed';
            if (upper === 'CHARMS') color = '#059669';

            return `<span style="background: ${color}; color: white; padding: 2px 6px; border-radius: 2px; margin-right: 4px; display: inline-block; font-size: 10px;">${tag}</span>`;
          })
          .join('');

        tagDisplay.innerHTML = `<strong>Tags:</strong> ${tagBadges}`;

        // Insert after product title
        const titleElement = item.element.querySelector('.cart-item__name, .cart-item__title');
        if (titleElement) {
          titleElement.parentNode.insertBefore(tagDisplay, titleElement.nextSibling);
        } else {
          // Fallback: append to item details
          const detailsElement = item.element.querySelector('.cart-item__details, .cart-item__info');
          if (detailsElement) {
            detailsElement.appendChild(tagDisplay);
          }
        }
      }
    });
  }

  showWarnings(nominationBracelets, totalBraceletQuantity, totalCharmQuantity) {
    // Skip if warnings are already being shown to prevent flickering
    if (this.warningsShouldBeShown) {
      return;
    }

    // Set flag immediately to prevent duplicate calls
    this.warningsShouldBeShown = true;

    // Clear existing warnings first
    this.clearAllWarnings();

    // Show warnings immediately without delay to reduce flickering
    this.showCartWarning(totalBraceletQuantity, totalCharmQuantity);
    this.hideCartFooter();
  }

  showItemWarning(bracelet, totalBraceletQuantity, totalCharmQuantity) {
    const warningElement = this.createWarningElement();
    warningElement.innerHTML = this.warningMessage;

    // Insert warning after the product details
    const detailsCell = bracelet.element.querySelector('.cart-item__details');
    if (detailsCell) {
      detailsCell.appendChild(warningElement);
    }
  }

  showCartWarning(totalBraceletQuantity, totalCharmQuantity) {
    // Show warning in cart footer as first full-width item
    this.showCartPageWarning();
    this.showCartDrawerWarning();
  }

  showCartPageWarning() {
    // Only show cart page warning if we're on the main cart page, not in drawer
    if (document.querySelector('#CartDrawer[open], .cart-drawer[open], .drawer[open]')) {
      return;
    }

    // Find cart footer on main cart page - prioritize .cart__footer
    const cartFooter = document.querySelector('.cart__footer');

    // Make sure we're not targeting cart drawer elements
    if (cartFooter && cartFooter.closest('#CartDrawer, .cart-drawer, .drawer')) {
      return;
    }

    if (cartFooter) {
      let warningElement = document.getElementById('nomination-cart-warning');
      if (!warningElement) {
        warningElement = document.createElement('div');
        warningElement.id = 'nomination-cart-warning';
        warningElement.className = 'nomination-cart-warning';
        // Insert as first child of cart footer
        cartFooter.insertBefore(warningElement, cartFooter.firstChild);
      }
      warningElement.innerHTML = this.warningMessage;
      warningElement.style.visibility = 'visible';

      // Ensure warning stays visible by adding a data attribute
      warningElement.setAttribute('data-nomination-warning', 'true');
    } else {
      // Fallback: try to find other cart containers
      const fallbackContainer = document.querySelector(
        ' .cart__dynamic-checkout-buttons, .cart__checkout, .cart__totals, .cart__footer-wrapper, .cart__content, #main-cart-items, .cart, .cart-page'
      );
      if (fallbackContainer) {
        let warningElement = document.getElementById('nomination-cart-warning');
        if (!warningElement) {
          warningElement = document.createElement('div');
          warningElement.id = 'nomination-cart-warning';
          warningElement.className = 'nomination-cart-warning';
          // Insert as first child of fallback container
          fallbackContainer.insertBefore(warningElement, fallbackContainer.firstChild);
        }
        warningElement.innerHTML = this.warningMessage;
        warningElement.style.visibility = 'visible';

        // Ensure warning stays visible by adding a data attribute
        warningElement.setAttribute('data-nomination-warning', 'true');
      }
    }
  }

  showCartDrawerWarning() {
    // Find cart drawer footer
    const drawerFooter = document.querySelector('.cart-drawer__footer');

    if (drawerFooter) {
      let warningElement = document.getElementById('nomination-cart-drawer-warning');
      if (!warningElement) {
        warningElement = document.createElement('div');
        warningElement.id = 'nomination-cart-drawer-warning';
        warningElement.className = 'nomination-cart-warning';
        // Insert as first child of cart drawer footer
        drawerFooter.insertBefore(warningElement, drawerFooter.firstChild);
      }
      warningElement.innerHTML = this.warningMessage;
      warningElement.style.display = 'block';

      // Ensure warning stays visible by adding a data attribute
      warningElement.setAttribute('data-nomination-warning', 'true');
    } else {
      // Fallback: try to find other drawer containers
      const fallbackContainer = document.querySelector(
        '#CartDrawer-CartItems, .drawer__footer, .cart-drawer, .drawer__content, #CartDrawer, .drawer__inner'
      );
      if (fallbackContainer) {
        let warningElement = document.getElementById('nomination-cart-drawer-warning');
        if (!warningElement) {
          warningElement = document.createElement('div');
          warningElement.id = 'nomination-cart-drawer-warning';
          warningElement.className = 'nomination-cart-warning';
          // Insert as first child of fallback container
          fallbackContainer.insertBefore(warningElement, fallbackContainer.firstChild);
        }
        warningElement.innerHTML = this.warningMessage;
        warningElement.style.display = 'block';

        // Ensure warning stays visible by adding a data attribute
        warningElement.setAttribute('data-nomination-warning', 'true');
      }
    }
  }

  createWarningElement() {
    const warning = document.createElement('div');
    warning.className = 'nomination-item-warning';

    // Add warning icon
    const icon = document.createElement('span');
    icon.innerHTML = '⚠️';
    warning.appendChild(icon);

    return warning;
  }

  clearAllWarnings() {
    // Remove item-level warnings
    document.querySelectorAll('.nomination-item-warning').forEach((warning) => {
      warning.remove();
    });

    // Hide cart page warning
    const cartWarning = document.getElementById('nomination-cart-warning');
    if (cartWarning) {
      cartWarning.style.visibility = 'hidden';
    }

    // Hide cart drawer warning
    const drawerWarning = document.getElementById('nomination-cart-drawer-warning');
    if (drawerWarning) {
      drawerWarning.style.display = 'none';
    }

    // Note: We're keeping debug tags display for testing - don't remove them here

    // Reset warning flag
    this.warningsShouldBeShown = false;

    // Show cart footer when clearing warnings
    this.showCartFooter();

    // Restore body overflow when clearing warnings
    this.restoreBodyOverflow();
  }

  // Method to force clear warnings (used when validation passes)
  forceClearWarnings() {
    this.warningsShouldBeShown = false;
    this.clearAllWarnings();
    this.restoreBodyOverflow();
  }

  clearItemWarning(bracelet) {
    // Clear warning for specific bracelet
    const detailsCell = bracelet.element.querySelector('.cart-item__details');
    if (detailsCell) {
      const existingWarning = detailsCell.querySelector('.nomination-item-warning');
      if (existingWarning) {
        existingWarning.remove();
      }
    }
  }

  disableCheckout() {
    // Disable checkout button in main cart (multiple selectors)
    const mainCheckoutSelectors = [
      'button[name="checkout"]',
      '#checkout',
      '.cart__checkout-button',
      'button[form="cart"]',
    ];

    let mainCheckoutButton = null;
    for (const selector of mainCheckoutSelectors) {
      mainCheckoutButton = document.querySelector(selector);
      if (mainCheckoutButton) break;
    }

    if (mainCheckoutButton) {
      mainCheckoutButton.disabled = true;
      mainCheckoutButton.setAttribute('data-nomination-disabled', 'true');
    }

    // Disable checkout button in cart drawer
    const drawerCheckoutButton = document.getElementById('CartDrawer-Checkout');
    if (drawerCheckoutButton) {
      drawerCheckoutButton.disabled = true;
      drawerCheckoutButton.setAttribute('data-nomination-disabled', 'true');
    }

    // Also disable any additional checkout buttons
    const additionalCheckoutButtons = document.querySelectorAll(
      '.additional-checkout-buttons button, .cart__dynamic-checkout-buttons button'
    );
    additionalCheckoutButtons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('data-nomination-disabled', 'true');
    });
  }

  enableCheckout() {
    // Enable checkout button in main cart (multiple selectors)
    const mainCheckoutSelectors = [
      'button[name="checkout"]',
      '#checkout',
      '.cart__checkout-button',
      'button[form="cart"]',
    ];

    let mainCheckoutButton = null;
    for (const selector of mainCheckoutSelectors) {
      mainCheckoutButton = document.querySelector(selector);
      if (mainCheckoutButton) break;
    }

    if (mainCheckoutButton) {
      mainCheckoutButton.disabled = false;
      mainCheckoutButton.removeAttribute('data-nomination-disabled');
    }

    // Enable checkout button in cart drawer
    const drawerCheckoutButton = document.getElementById('CartDrawer-Checkout');
    if (drawerCheckoutButton) {
      drawerCheckoutButton.disabled = false;
      drawerCheckoutButton.removeAttribute('data-nomination-disabled');
    }

    // Also enable any additional checkout buttons
    const additionalCheckoutButtons = document.querySelectorAll(
      '.additional-checkout-buttons button, .cart__dynamic-checkout-buttons button'
    );
    additionalCheckoutButtons.forEach((button) => {
      button.disabled = false;
      button.removeAttribute('data-nomination-disabled');
    });
  }

  hideCartFooter() {
    // Hide checkout buttons and CTAs only on main cart page when warnings are showing
    // Keep subtotal and taxes visible

    // Hide checkout CTAs
    const cartCtas = document.querySelectorAll('.cart__ctas');
    cartCtas.forEach((cta) => {
      cta.style.display = 'none';
    });

    // Hide dynamic checkout buttons
    const dynamicCheckoutButtons = document.querySelectorAll(
      '.cart__dynamic-checkout-buttons, .additional-checkout-buttons'
    );
    dynamicCheckoutButtons.forEach((buttons) => {
      buttons.style.display = 'none';
    });

    // Hide individual checkout buttons
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], #checkout, .cart__checkout-button, button[form="cart"]'
    );
    checkoutButtons.forEach((button) => {
      button.style.display = 'none';
    });

    // Keep cart drawer footer visible but disable checkout button
    // (This is handled in disableCheckout method)
  }

  showCartFooter() {
    // Show checkout buttons and CTAs when warnings are cleared
    // Keep subtotal and taxes visible

    // Show checkout CTAs
    const cartCtas = document.querySelectorAll('.cart__ctas');
    cartCtas.forEach((cta) => {
      cta.style.display = '';
    });

    // Show dynamic checkout buttons
    const dynamicCheckoutButtons = document.querySelectorAll(
      '.cart__dynamic-checkout-buttons, .additional-checkout-buttons'
    );
    dynamicCheckoutButtons.forEach((buttons) => {
      buttons.style.display = '';
    });

    // Show individual checkout buttons
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], #checkout, .cart__checkout-button, button[form="cart"]'
    );
    checkoutButtons.forEach((button) => {
      button.style.display = '';
    });

    // Cart drawer footer is always visible, just enable/disable checkout
    // (This is handled in enableCheckout method)
  }

  // Removed aggressive validation - now only validates on specific events

  // Force cart refresh to ensure DOM is updated
  forceCartRefresh() {
    // Use Cart Manager if available
    if (this.cartManager) {
      this.cartManager
        .forceRefresh()
        .then(() => {
          this.validateCart();
          this.restoreBodyOverflow();
        })
        .catch((error) => {
          this.validateCart();
          this.restoreBodyOverflow();
        });
      return;
    }

    // Clear any existing cart cache
    if (window.cart && typeof window.cart.clearCache === 'function') {
      window.cart.clearCache();
    }

    // Force fetch fresh cart data with cache busting
    const cacheBuster = Date.now();
    fetch(`/cart.js?t=${cacheBuster}`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
      .then((response) => response.json())
      .then((cartData) => {
        // Trigger validation after getting fresh data
        setTimeout(() => {
          this.validateCart();
          this.restoreBodyOverflow();
        }, 100);
      })
      .catch((error) => {
        // Fallback to regular validation
        this.validateCart();
        this.restoreBodyOverflow();
      });

    // Try to trigger cart refresh via Shopify's cart system
    if (window.cart && typeof window.cart.refresh === 'function') {
      window.cart.refresh();
    }

    // Try to trigger cart update event
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }

    // Force validation after refresh
    setTimeout(() => {
      this.validateCart();
      this.restoreBodyOverflow();
    }, 200);
  }

  // Cleanup method
  destroy() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
    }
    if (this.removeValidationInterval) {
      clearInterval(this.removeValidationInterval);
    }
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }

    // Clean up Cart Manager subscriptions
    if (this.unsubscribeCartUpdated) this.unsubscribeCartUpdated();
    if (this.unsubscribeCartApiCall) this.unsubscribeCartApiCall();
    if (this.unsubscribeQuantityChange) this.unsubscribeQuantityChange();
    if (this.unsubscribeItemRemove) this.unsubscribeItemRemove();
    if (this.unsubscribeDomChanged) this.unsubscribeDomChanged();
    if (this.unsubscribeFormSubmit) this.unsubscribeFormSubmit();
  }
}

// Initialize the validation when the script loads
const nominationValidation = new NominationCartValidation();

// Expose debugging method to global scope for testing
window.nominationValidation = {
  enableDebug: () => {
    nominationValidation.debug = true;
    console.log('Nomination cart validation debugging enabled');
  },
  disableDebug: () => {
    nominationValidation.debug = false;
    console.log('Nomination cart validation debugging disabled');
  },
  showTags: () => {
    nominationValidation.showDebugTags = true;
    const cartItems = nominationValidation.getCartItems();
    nominationValidation.displayTagsOnCartItems(cartItems);
    console.log('Debug tags enabled and displayed on cart items');
  },
  hideTags: () => {
    nominationValidation.showDebugTags = false;
    document.querySelectorAll('.debug-tags-display').forEach((tag) => tag.remove());
    console.log('Debug tags disabled and hidden');
  },
  validate: () => {
    nominationValidation.validateCart();
  },
  forceValidate: () => {
    console.log('Forcing validation...');
    nominationValidation.validateCart();
  },
  testCartChanges: () => {
    console.log('Testing cart changes...');
    console.log('Current cart items:', nominationValidation.getCartItems());
    console.log(
      'Nomination bracelets:',
      nominationValidation.getNominationBracelets(nominationValidation.getCartItems())
    );
    console.log('Charms:', nominationValidation.getCharms(nominationValidation.getCartItems()));
    nominationValidation.validateCart();
  },
  debugValidation: () => {
    console.log('=== DEBUGGING VALIDATION ===');
    const cartItems = nominationValidation.getCartItems();
    console.log('Total cart items found:', cartItems.length);

    cartItems.forEach((item, index) => {
      const tags = nominationValidation.getProductTagsFromElement(item.element);
      console.log(`Item ${index + 1}:`, {
        title: item.title,
        quantity: item.quantity,
        tags: tags,
        isNominationBracelet: tags.includes('nomination') && (tags.includes('bracelet') || tags.includes('bracelets')),
        isCharm:
          (tags.includes('charm') || tags.includes('charms')) &&
          !(tags.includes('nomination') && (tags.includes('bracelet') || tags.includes('bracelets'))),
      });
    });

    const nominationBracelets = nominationValidation.getNominationBracelets(cartItems);
    const charms = nominationValidation.getCharms(cartItems);

    console.log('Nomination bracelets found:', nominationBracelets.length);
    console.log('Charms found:', charms.length);

    if (nominationBracelets.length > 0) {
      const totalBraceletQuantity = nominationValidation.getTotalBraceletQuantity(nominationBracelets);
      const totalCharmQuantity = nominationValidation.getTotalCharmQuantity(charms);
      console.log('Total bracelet quantity:', totalBraceletQuantity);
      console.log('Total charm quantity:', totalCharmQuantity);
      console.log('Should show warnings:', totalCharmQuantity < totalBraceletQuantity);
    }

    nominationValidation.validateCart();
  },
  testRemoveAction: () => {
    console.log('Testing remove action validation...');
    console.log('Current state before remove:', {
      cartItems: nominationValidation.getCartItems().length,
      nominationBracelets: nominationValidation.getNominationBracelets(nominationValidation.getCartItems()).length,
      charms: nominationValidation.getCharms(nominationValidation.getCartItems()).length,
    });
    nominationValidation.validateCart();
  },
  testRemoveButtons: () => {
    console.log('Testing remove button detection...');
    const removeButtons = document.querySelectorAll('cart-remove-button');
    console.log('Found remove buttons:', removeButtons.length);
    removeButtons.forEach((button, index) => {
      console.log(`Remove button ${index + 1}:`, {
        id: button.id,
        hasListener: button.hasAttribute('data-nomination-listener'),
        innerHTML: button.innerHTML,
      });
    });
  },
  forceRefreshValidation: () => {
    console.log('Force refreshing validation...');
    // Clear all warnings first
    nominationValidation.clearAllWarnings();
    // Force validation multiple times
    nominationValidation.validateCart();
    setTimeout(() => nominationValidation.validateCart(), 100);
    setTimeout(() => nominationValidation.validateCart(), 500);
    setTimeout(() => nominationValidation.validateCart(), 1000);
  },
  waitAndValidate: (delay = 1000) => {
    console.log(`Waiting ${delay}ms then validating...`);
    setTimeout(() => {
      console.log('Delayed validation running');
      nominationValidation.validateCart();
    }, delay);
  },
  forceCartRefresh: () => {
    console.log('Manually forcing cart refresh...');
    nominationValidation.forceCartRefresh();
  },
  getCartItems: () => {
    return nominationValidation.getCartItems();
  },
  getNominationBracelets: () => {
    const cartItems = nominationValidation.getCartItems();
    return nominationValidation.getNominationBracelets(cartItems);
  },
  getCharms: () => {
    const cartItems = nominationValidation.getCartItems();
    return nominationValidation.getCharms(cartItems);
  },
  checkCheckoutButtons: () => {
    const buttons = {
      mainCart: document.querySelector('button[name="checkout"]'),
      mainCartById: document.getElementById('checkout'),
      mainCartByClass: document.querySelector('.cart__checkout-button'),
      mainCartByForm: document.querySelector('button[form="cart"]'),
      drawer: document.getElementById('CartDrawer-Checkout'),
      additional: document.querySelectorAll(
        '.additional-checkout-buttons button, .cart__dynamic-checkout-buttons button'
      ),
    };

    console.log('Checkout buttons found:', {
      mainCart: !!buttons.mainCart,
      mainCartById: !!buttons.mainCartById,
      mainCartByClass: !!buttons.mainCartByClass,
      mainCartByForm: !!buttons.mainCartByForm,
      drawer: !!buttons.drawer,
      additional: buttons.additional.length,
      allButtons: Object.values(buttons).filter((btn) => btn && (btn.length === undefined ? true : btn.length > 0)),
    });

    return buttons;
  },
  restoreOverflow: () => {
    console.log('Manually restoring body overflow...');
    nominationValidation.restoreBodyOverflow();
    console.log('Body overflow restored');
  },
};
